import { useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { Container } from "react-bootstrap";
import { useRouteContext } from "@tanstack/react-router";
import { SASS } from "@/models/stff";
import FormFieldRow from "@/components/shared/forms/FormFieldRow";
import ReadOnlySelect from "@/components/shared/forms/ReadOnlySelect";
import Select from "@/components/shared/forms/Select";
import TextInput from "@/components/shared/forms/TextInput";
import { useCoreData } from "@/queries.ts";
import { FiUsers } from "react-icons/fi";
import {
  FaBriefcase,
  FaChalkboardTeacher,
  FaTrashAlt,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

type SASSWizardFormProps = {
  form: UseFormReturn<SASS, any, SASS>;
  assignmentNumber?: number;
  onRemove?: () => void;
};

export function SASSWizardForm({
  form,
  assignmentNumber = 1,
  onRemove,
}: SASSWizardFormProps) {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const viewOnly = false;
  const coreData = useCoreData();
  const tooltipMap = leaContext.coreData.tooltipMap.get("SASS");

  const idPrefix = `assignment-${assignmentNumber}-`;

  const {
    control,
    register,
    watch,
    formState: { errors },
  } = form;

  const programYear = watch("ProgramYear");
  const classroomRole1 = watch("ClassroomRole1");
  const classroomRole2 = watch("ClassroomRole2");
  const nonClassroomRole1 = watch("NonClassroomRole1");
  const nonClassroomRole2 = watch("NonClassroomRole2");
  const nonClassroomRole3 = watch("NonClassroomRole3");

  const [collapsed, setCollapsed] = useState(false);

  const hasErrors = Object.keys(errors).length > 0;
  useEffect(() => {
    if (hasErrors) setCollapsed(false);
  }, [hasErrors]);

  const validatePercentage = (
    value: number | string | undefined,
    roleValue: string | undefined
  ) => {
    if (!roleValue || roleValue === "") {
      return true;
    }

    if (
      value === undefined ||
      value === null ||
      value === "" ||
      value === 0 ||
      value === "0"
    ) {
      return "Percentage is required when role is selected";
    }

    const num = typeof value === "string" ? Number(value) : value;

    if (isNaN(num)) {
      return "Must be a valid number";
    }

    if (!Number.isInteger(num)) {
      return "Must be an integer (no decimals)";
    }

    if (num < 1 || num > 200) {
      return "Must be between 1 and 200";
    }

    return true;
  };

  const Wrapper = assignmentNumber === 1 ? Container : "div";
  const wrapperProps =
    assignmentNumber === 1
      ? {
          className: "form-style overview-container ps-3 pe-3",
          style: { borderTop: "none", borderRadius: 0 } as React.CSSProperties,
        }
      : {
          className: "ps-3 pe-3",
        };

  const headerStyle = {
    backgroundColor: "#F9FAFB",
    padding: "14px 20px",
    userSelect: "none" as const,
  };

  return (
    <Wrapper {...wrapperProps}>
      <div
        className="form-card-modern mb-3"
        style={{
          overflow: "hidden",
          padding: 0,
          ...(onRemove
            ? {
                border: "2px dashed #c5ccd4",
                borderRadius: "8px",
              }
            : {}),
        }}
      >
        <div
          className="section-header d-flex justify-content-between align-items-center"
          style={{
            ...headerStyle,
            width: "100%",
            ...(collapsed ? { marginBottom: 0 } : {}),
          }}
        >
          <h2 className="section-title mb-0 flex-grow-1">
            <button
              type="button"
              className="d-flex align-items-center gap-2 w-100"
              style={{
                border: "none",
                background: "none",
                font: "inherit",
                textAlign: "left" as const,
                padding: 0,
                cursor: "pointer",
              }}
              onClick={() => setCollapsed((c) => !c)}
              aria-expanded={!collapsed}
            >
              <FaBriefcase className="section-icon" aria-hidden="true" />
              {assignmentNumber === 1
                ? "Staff Assignment"
                : `Staff Assignment ${assignmentNumber}`}
            </button>
          </h2>
          <div className="d-flex align-items-center gap-2">
            {onRemove && (
              <button
                type="button"
                className="btn btn-sm d-flex align-items-center gap-1"
                style={{
                  color: "#b02a37",
                  border: "none",
                  background: "transparent",
                  fontWeight: 500,
                  fontSize: "13px",
                  cursor: "pointer",
                }}
                onClick={onRemove}
                aria-label={
                  assignmentNumber === 1
                    ? "Remove Staff Assignment"
                    : `Remove Staff Assignment ${assignmentNumber}`
                }
              >
                <FaTrashAlt style={{ fontSize: "12px" }} />
                Remove
              </button>
            )}
            <button
              type="button"
              className="btn btn-sm d-flex align-items-center"
              style={{
                border: "none",
                background: "transparent",
                padding: 0,
                cursor: "pointer",
              }}
              onClick={() => setCollapsed((c) => !c)}
              aria-expanded={!collapsed}
              aria-label={
                collapsed
                  ? assignmentNumber === 1
                    ? "Expand Staff Assignment"
                    : `Expand Staff Assignment ${assignmentNumber}`
                  : assignmentNumber === 1
                    ? "Collapse Staff Assignment"
                    : `Collapse Staff Assignment ${assignmentNumber}`
              }
            >
              {collapsed ? (
                <FaChevronDown style={{ fontSize: "12px", color: "#6c757d" }} />
              ) : (
                <FaChevronUp style={{ fontSize: "12px", color: "#6c757d" }} />
              )}
            </button>
          </div>
        </div>

        {!collapsed && (
          <div style={{ padding: "20px" }}>
            <div className="form-card-modern mb-3">
              <div className="section-header">
                <FiUsers className="section-icon" />
                <h3 className="section-title">Staff Information</h3>
              </div>

              <div className="row g-3">
                <div className="col-md-4">
                  <FormFieldRow
                    name="PreschoolCode"
                    labelText="Preschool"
                    errors={errors}
                    tooltipMap={tooltipMap}
                    labelCol={12}
                    inputCol={12}
                    isRequired
                    idPrefix={idPrefix}
                  >
                    {(id, name) => (
                      <Select
                        id={id}
                        disabled={viewOnly}
                        {...register(name, {
                          required: "Preschool is required",
                        })}
                        options={leaContext
                          .currentOrgPreschoolsByProgramYear(programYear)
                          .map((x) => {
                            return {
                              Text: x.PreschoolName,
                              Value: x.PreschoolCDSCode,
                            };
                          })}
                      />
                    )}
                  </FormFieldRow>
                </div>
                <div className="col-md-4">
                  <FormFieldRow
                    name="ProgramYear"
                    labelText="Program Year"
                    errors={errors}
                    tooltipMap={tooltipMap}
                    labelCol={12}
                    inputCol={12}
                    isRequired
                    idPrefix={idPrefix}
                  >
                    {(id, name) => (
                      <ReadOnlySelect
                        id={id}
                        control={control}
                        name={name}
                        rules={{ required: "Program Year is required" }}
                        options={coreData.programYearList}
                      />
                    )}
                  </FormFieldRow>
                </div>
                <div className="col-md-4">
                  <FormFieldRow
                    name="LocalStaffId"
                    labelText="Staff Local ID"
                    errors={errors}
                    tooltipMap={tooltipMap}
                    labelCol={12}
                    inputCol={12}
                    isRequired
                    idPrefix={idPrefix}
                  >
                    {(id, name) => (
                      <TextInput
                        id={id}
                        readOnly
                        maxLength={50}
                        {...register(name, {
                          required: "Staff Local ID is required",
                        })}
                      />
                    )}
                  </FormFieldRow>
                </div>
              </div>
            </div>

            <div className="form-card-modern mb-3">
              <div className="section-header">
                <FaChalkboardTeacher className="section-icon" />
                <h3 className="section-title">Classroom Roles</h3>
              </div>

              <div className="row g-3">
                <div className="col-md-6">
                  <FormFieldRow
                    name="ClassroomRole1"
                    labelText="Role 1"
                    errors={errors}
                    tooltipMap={tooltipMap}
                    labelCol={12}
                    inputCol={12}
                    idPrefix={idPrefix}
                  >
                    {(id, name) => (
                      <Select
                        id={id}
                        disabled={viewOnly}
                        {...register(name)}
                        options={coreData.staffRoleList}
                      />
                    )}
                  </FormFieldRow>
                </div>
                <div className="col-md-6">
                  <FormFieldRow
                    name="ClassroomRole1PercentOfTime"
                    labelText="Role 1 Full-Time Equivalency"
                    errors={errors}
                    tooltipMap={tooltipMap}
                    labelCol={12}
                    inputCol={12}
                    isRequired={!!classroomRole1}
                    idPrefix={idPrefix}
                  >
                    {(id, name) => (
                      <TextInput
                        id={id}
                        disabled={viewOnly}
                        maxLength={9}
                        {...register(name, {
                          validate: (value) =>
                            validatePercentage(value, classroomRole1),
                        })}
                      />
                    )}
                  </FormFieldRow>
                </div>
              </div>
              <div className="row g-3">
                <div className="col-md-6">
                  <FormFieldRow
                    name="ClassroomRole2"
                    labelText="Role 2"
                    errors={errors}
                    tooltipMap={tooltipMap}
                    labelCol={12}
                    inputCol={12}
                    idPrefix={idPrefix}
                  >
                    {(id, name) => (
                      <Select
                        id={id}
                        disabled={viewOnly}
                        {...register(name)}
                        options={coreData.staffRoleList}
                      />
                    )}
                  </FormFieldRow>
                </div>
                <div className="col-md-6">
                  <FormFieldRow
                    name="ClassroomRole2PercentOfTime"
                    labelText="Role 2 Full-Time Equivalency"
                    errors={errors}
                    tooltipMap={tooltipMap}
                    labelCol={12}
                    inputCol={12}
                    isRequired={!!classroomRole2}
                    idPrefix={idPrefix}
                  >
                    {(id, name) => (
                      <TextInput
                        id={id}
                        disabled={viewOnly}
                        maxLength={9}
                        {...register(name, {
                          validate: (value) =>
                            validatePercentage(value, classroomRole2),
                        })}
                      />
                    )}
                  </FormFieldRow>
                </div>
              </div>
            </div>

            <div className="form-card-modern mb-3">
              <div className="section-header">
                <FaBriefcase className="section-icon" />
                <h3 className="section-title">Non-Classroom Roles</h3>
              </div>

              <div className="row g-3">
                <div className="col-md-6">
                  <FormFieldRow
                    name="NonClassroomRole1"
                    labelText="Role 1"
                    errors={errors}
                    tooltipMap={tooltipMap}
                    labelCol={12}
                    inputCol={12}
                    idPrefix={idPrefix}
                  >
                    {(id, name) => (
                      <Select
                        id={id}
                        disabled={viewOnly}
                        {...register(name)}
                        options={coreData.nonClassroomRoleList}
                      />
                    )}
                  </FormFieldRow>
                </div>
                <div className="col-md-6">
                  <FormFieldRow
                    name="NonClassroomRole1PercentOfTime"
                    labelText="Role 1 Full-Time Equivalency"
                    errors={errors}
                    tooltipMap={tooltipMap}
                    labelCol={12}
                    inputCol={12}
                    isRequired={!!nonClassroomRole1}
                    idPrefix={idPrefix}
                  >
                    {(id, name) => (
                      <TextInput
                        id={id}
                        disabled={viewOnly}
                        maxLength={9}
                        {...register(name, {
                          validate: (value) =>
                            validatePercentage(value, nonClassroomRole1),
                        })}
                      />
                    )}
                  </FormFieldRow>
                </div>
              </div>
              <div className="row g-3">
                <div className="col-md-6">
                  <FormFieldRow
                    name="NonClassroomRole2"
                    labelText="Role 2"
                    errors={errors}
                    tooltipMap={tooltipMap}
                    labelCol={12}
                    inputCol={12}
                    idPrefix={idPrefix}
                  >
                    {(id, name) => (
                      <Select
                        id={id}
                        disabled={viewOnly}
                        {...register(name)}
                        options={coreData.nonClassroomRoleList}
                      />
                    )}
                  </FormFieldRow>
                </div>
                <div className="col-md-6">
                  <FormFieldRow
                    name="NonClassroomRole2PercentOfTime"
                    labelText="Role 2 Full-Time Equivalency"
                    errors={errors}
                    tooltipMap={tooltipMap}
                    labelCol={12}
                    inputCol={12}
                    isRequired={!!nonClassroomRole2}
                    idPrefix={idPrefix}
                  >
                    {(id, name) => (
                      <TextInput
                        id={id}
                        disabled={viewOnly}
                        maxLength={9}
                        {...register(name, {
                          validate: (value) =>
                            validatePercentage(value, nonClassroomRole2),
                        })}
                      />
                    )}
                  </FormFieldRow>
                </div>
              </div>

              <div className="row g-3">
                <div className="col-md-6">
                  <FormFieldRow
                    name="NonClassroomRole3"
                    labelText="Role 3"
                    errors={errors}
                    tooltipMap={tooltipMap}
                    labelCol={12}
                    inputCol={12}
                    idPrefix={idPrefix}
                  >
                    {(id, name) => (
                      <Select
                        id={id}
                        disabled={viewOnly}
                        {...register(name)}
                        options={coreData.nonClassroomRoleList}
                      />
                    )}
                  </FormFieldRow>
                </div>
                <div className="col-md-6">
                  <FormFieldRow
                    name="NonClassroomRole3PercentOfTime"
                    labelText="Role 3 Full-Time Equivalency"
                    errors={errors}
                    tooltipMap={tooltipMap}
                    labelCol={12}
                    inputCol={12}
                    isRequired={!!nonClassroomRole3}
                    idPrefix={idPrefix}
                  >
                    {(id, name) => (
                      <TextInput
                        id={id}
                        disabled={viewOnly}
                        maxLength={9}
                        {...register(name, {
                          validate: (value) =>
                            validatePercentage(value, nonClassroomRole3),
                        })}
                      />
                    )}
                  </FormFieldRow>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Wrapper>
  );
}
